import type { Option } from './types';

// Filter options based on input value
export const useHandleInputChange = (
  options: Option[],
  setInputValue: (value: string) => void,
  setFilteredOptions: (options: Option[]) => void,
  setIsOpen: (isOpen: boolean) => void,
  selectedOptions: Option[]
) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setFilteredOptions(
      options.filter(
        (option) =>
          option.name.toLowerCase().includes(value.toLowerCase()) &&
          !selectedOptions.some((o) => o.id === option.id)
      )
    );
    setIsOpen(true);
  };

  return {
    handleInputChange,
  };
};

// Add or remove option from selectedOptions
export const useHandleOptionClick = (
  selectedOptions: Option[],
  setSelectedOptions: (options: Option[]) => void,
  setInputValue: (value: string) => void,
  onSelect: (options: Option[]) => void
) => {
  const handleOptionClick = (option: Option) => {
    let newSelected;
    if (selectedOptions.some((o) => o.id === option.id)) {
      newSelected = selectedOptions.filter((o) => o.id !== option.id);
    } else {
      newSelected = [...selectedOptions, option];
    }
    setSelectedOptions(newSelected);
    setInputValue('');
    onSelect(newSelected);
  };

  return {
    handleOptionClick,
  };
};

// Remove option from selectedOptions
export const useHandleRemoveOption = (
  selectedOptions: Option[],
  setSelectedOptions: (options: Option[]) => void,
  onSelect: (options: Option[]) => void
) => {
  const handleRemoveOption = (option: Option) => {
    const newSelected = selectedOptions.filter((o) => o.id !== option.id);
    setSelectedOptions(newSelected);
    onSelect(newSelected);
  };

  return {
    handleRemoveOption,
  };
};

// Keyboard navigation for the dropdown
export const useHandleInputKeyDown = (
  inputValue: string,
  selectedOptions: Option[],
  handleRemoveOption: (option: Option) => void,
  setIsOpen: (isOpen: boolean) => void,
  setHighlightedIndex: (index: number) => void,
  highlightedIndex: number,
  filteredOptions: Option[],
  handleOptionClick: (option: Option) => void,
  isOpen: boolean
) => {
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && inputValue === '' && selectedOptions.length > 0) {
      handleRemoveOption(selectedOptions[selectedOptions.length - 1]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(highlightedIndex < filteredOptions.length - 1 ? highlightedIndex + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(highlightedIndex > 0 ? highlightedIndex - 1 : filteredOptions.length - 1);
    } else if (e.key === 'Enter' && isOpen && filteredOptions.length > 0) {
      e.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) {
        handleOptionClick(option);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return {
    handleInputKeyDown,
  };
};
